/**
 * =============================================================================
 * CASE DETAILS - CONTACT / CARE TEAM / CASE LOG PANELS
 * =============================================================================
 *
 * The three non journey tabs. They render whatever `caseDataAdapters` hands
 * them, so a renamed or missing API field is handled in the adapter rather
 * than here.
 * =============================================================================
 */

import React, { useMemo, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
    getCareTeam,
    getCaseLogs,
    getContactGroups,
} from "./caseDataAdapters";
import { FONT, JOURNEY_COLORS, inputSx, sectionTitleSx } from "./journeyStyles";

/* -----------------------------------------------------------------------------
   SHARED
----------------------------------------------------------------------------- */

const groupLabelSx = {
    fontFamily: FONT,
    fontSize: "11px",
    lineHeight: "16px",
    fontWeight: 600,
    letterSpacing: "0.7px",
    color: JOURNEY_COLORS.textMuted,
    marginBottom: "8px",
};

const emptyTextSx = {
    fontFamily: FONT,
    fontSize: "11px",
    lineHeight: "16px",
    color: JOURNEY_COLORS.textMuted,
};

const PanelHeading = ({ children }) => (
    <Typography sx={sectionTitleSx}>{children}</Typography>
);

/* =============================================================================
   CONTACT DETAILS
============================================================================= */

const ContactItem = ({ contact }) => (
    <Box
        sx={{
            minWidth: {
                xs: "130px",
                sm: "150px",
                md: "170px",
            },
        }}
    >
        <Typography
            sx={{
                fontFamily: FONT,
                fontSize: "12px",
                lineHeight: "18px",
                fontWeight: 500,
                color: JOURNEY_COLORS.text,
            }}
        >
            {contact?.name || "--"}
        </Typography>

        <Typography
            sx={{
                fontFamily: FONT,
                fontSize: "12px",
                lineHeight: "18px",
                fontWeight: 400,
                color: JOURNEY_COLORS.textMuted,
            }}
        >
            {contact?.phone || "--"}
        </Typography>
    </Box>
);

export const ContactDetailsPanel = ({ caseData }) => {
    const groups = useMemo(
        () => getContactGroups(caseData),
        [caseData]
    );

    return (
        <Box sx={{ width: "100%" }}>
            <PanelHeading>Contact Details</PanelHeading>

            <Box
                sx={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr 1fr",
                    },
                    columnGap: {
                        xs: "20px",
                        md: "80px",
                    },
                    rowGap: "24px",
                }}
            >
                {groups.map((group) => (
                    <Box key={group.id}>
                        <Typography sx={groupLabelSx}>
                            {group.label}
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                columnGap: { xs: "20px", sm: "32px", md: "48px" },
                                rowGap: "14px",
                            }}
                        >
                            {group.contacts.length > 0 ? (
                                group.contacts.map((contact, index) => (
                                    <ContactItem
                                        key={
                                            group.id + "-" + index
                                        }
                                        contact={contact}
                                    />
                                ))
                            ) : (
                                <ContactItem />
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

/* =============================================================================
   CARE TEAM
============================================================================= */

export const CareTeamPanel = ({
    caseData,
    peoples = [],
    isLoading = false,
}) => {
    const [search, setSearch] = useState("");

    const doctors = useMemo(
        () => getCareTeam(caseData, peoples),
        [caseData, peoples]
    );

    const filteredDoctors = useMemo(() => {
        const term = search.trim().toLowerCase();

        if (!term) {
            return doctors;
        }

        return doctors.filter((doctor) =>
            doctor.name.toLowerCase().includes(term)
        );
    }, [doctors, search]);

    return (
        <Box sx={{ width: "100%" }}>
            <PanelHeading>Care Team</PanelHeading>

            <Box
                sx={{
                    width: "100%",
                    backgroundColor: JOURNEY_COLORS.track,
                    borderRadius: "8px",
                    padding: "10px",
                    boxSizing: "border-box",
                }}
            >
                {/* SEARCH */}

                <TextField
                    fullWidth
                    size="small"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search doctors"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    sx={{
                                        fontSize: 15,
                                        color: JOURNEY_COLORS.textMuted,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        ...inputSx,
                        "& .MuiOutlinedInput-root": {
                            ...inputSx["& .MuiOutlinedInput-root"],
                            height: "36px",
                            fontSize: "11px",
                        },
                    }}
                />

                {/* DOCTORS */}

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        marginTop: "10px",
                    }}
                >
                    {isLoading && (
                        <Typography sx={emptyTextSx}>
                            Loading doctors...
                        </Typography>
                    )}

                    {!isLoading && filteredDoctors.length === 0 && (
                        <Typography sx={emptyTextSx}>
                            {doctors.length === 0
                                ? "No doctors assigned"
                                : "No doctors match your search"}
                        </Typography>
                    )}

                    {!isLoading &&
                        filteredDoctors.map((doctor) => (
                            <Box
                                key={doctor.id}
                                sx={{
                                    width: {
                                        xs: "100%",
                                        sm: "170px",
                                    },
                                    minHeight: "46px",
                                    backgroundColor:
                                        JOURNEY_COLORS.surface,
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "7px 8px",
                                    boxSizing: "border-box",
                                }}
                            >
                                <Avatar
                                    src={doctor.profileImage || ""}
                                    sx={{
                                        width: "28px",
                                        height: "28px",
                                        backgroundColor:
                                            JOURNEY_COLORS.border,
                                        fontFamily: FONT,
                                        fontSize: "10px",
                                        fontWeight: 600,
                                        color: JOURNEY_COLORS.textMuted,
                                    }}
                                >
                                    {!doctor.profileImage &&
                                        doctor.initials}
                                </Avatar>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        title={doctor.displayName}
                                        sx={{
                                            fontFamily: FONT,
                                            fontSize: "11px",
                                            lineHeight: "14px",
                                            fontWeight: 600,
                                            color: JOURNEY_COLORS.text,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {doctor.displayName}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily: FONT,
                                            fontSize: "9px",
                                            lineHeight: "12px",
                                            fontWeight: 600,
                                            color: doctor.statusInfo
                                                .color,
                                        }}
                                    >
                                        {doctor.statusInfo.label}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                </Box>
            </Box>
        </Box>
    );
};

/* =============================================================================
   CASE LOG
============================================================================= */

export const CaseLogPanel = ({ caseData }) => {
    const logs = useMemo(() => getCaseLogs(caseData), [caseData]);

    return (
        <Box sx={{ width: "100%" }}>
            <PanelHeading>Case Log</PanelHeading>

            <Box
                sx={{
                    width: "100%",
                    backgroundColor: JOURNEY_COLORS.track,
                    borderRadius: "8px",
                    padding: { xs: "12px", sm: "16px" },
                    boxSizing: "border-box",
                }}
            >
                {logs.length === 0 && (
                    <Typography sx={emptyTextSx}>
                        No case log available
                    </Typography>
                )}

                {logs.map((log, index) => {
                    const isLast = index === logs.length - 1;

                    return (
                        <Box
                            key={log.id}
                            sx={{
                                position: "relative",
                                display: "flex",
                                gap: "12px",
                                paddingBottom: isLast ? 0 : "18px",
                            }}
                        >
                            {/* TIMELINE RAIL */}

                            <Box
                                sx={{
                                    width: "14px",
                                    minWidth: "14px",
                                    display: "flex",
                                    justifyContent: "center",
                                    position: "relative",
                                    paddingTop: "4px",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "10px",
                                        height: "10px",
                                        minHeight: "10px",
                                        border:
                                            "2px solid " +
                                            JOURNEY_COLORS.success,
                                        borderRadius: "50%",
                                        backgroundColor:
                                            JOURNEY_COLORS.surface,
                                        zIndex: 1,
                                    }}
                                />

                                {!isLast && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "14px",
                                            bottom: "-18px",
                                            width: "1px",
                                            borderLeft:
                                                "1px dashed " +
                                                JOURNEY_COLORS.success,
                                        }}
                                    />
                                )}
                            </Box>

                            {/* CONTENT */}

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT,
                                        fontSize: "11px",
                                        lineHeight: "16px",
                                        color: JOURNEY_COLORS.textMuted,
                                    }}
                                >
                                    {log.date || "--"}

                                    {log.actionLabel
                                        ? " : " + log.actionLabel
                                        : ""}
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: FONT,
                                            fontSize: "12px",
                                            lineHeight: "17px",
                                            fontWeight: 500,
                                            color: JOURNEY_COLORS.text,
                                        }}
                                    >
                                        {log.message ||
                                            log.actionLabel ||
                                            "--"}

                                        {log.user
                                            ? " by " + log.user
                                            : ""}
                                    </Typography>

                                    {log.isClosing && (
                                        <CheckCircleIcon
                                            sx={{
                                                fontSize: "14px",
                                                color: JOURNEY_COLORS.success,
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};
